<template>
  <div class="company-profile-layout">
    <!-- Sidebar -->
    <Sidebar />

    <!-- Main Content -->
    <div class="main-content">
      <!-- Top Header -->
      <TopHeader />

      <!-- Page Content -->
      <main class="page-content">
        <div class="profile-container">
          <!-- Page Header -->
          <div class="page-header">
            <div class="header-content">
              <h1 class="page-title">
                <span class="page-icon">🏢</span>
                Company Profile
              </h1>
              <p class="page-description">Manage your company information and settings</p>
            </div>
          </div>

          <!-- Profile Form -->
          <div class="profile-form-container">
            <div v-if="loading" class="loading-state">
              <div class="loading-spinner"></div>
              <p>Loading company profile...</p>
            </div>

            <form v-else @submit.prevent="updateProfile" class="profile-form">
              <!-- Contact Information -->
              <div class="form-section">
                <h3 class="section-title">
                  <span class="section-icon">📞</span>
                  Contact Information
                </h3>

                <div class="form-row">
                  <div class="form-group">
                    <label for="address" class="form-label">Address</label>
                    <input
                      id="address"
                      v-model="profileForm.address"
                      type="text"
                      class="form-input"
                      placeholder="Enter company address"
                      required
                    />
                  </div>

                  <div class="form-group">
                    <label for="phone" class="form-label">Phone Number</label>
                    <input
                      id="phone"
                      v-model="profileForm.phone"
                      type="tel"
                      class="form-input"
                      placeholder="Enter phone number"
                      required
                    />
                  </div>
                </div>

                <div class="form-group">
                  <label for="location_url" class="form-label">
                    <span class="field-icon">📍</span>
                    Location URL
                  </label>
                  <input
                    id="location_url"
                    v-model="profileForm.location_url"
                    type="url"
                    class="form-input"
                    placeholder="https://maps.google.com/..."
                  />
                  <p class="form-help">Google Maps link or other location URL</p>
                </div>
              </div>

              <!-- Working Hours -->
              <div class="form-section">
                <h3 class="section-title">
                  <span class="section-icon">⏰</span>
                  Working Hours
                </h3>

                <div class="form-row">
                  <div class="form-group">
                    <label for="customer_service_work_hours" class="form-label">Customer Service Hours</label>
                    <input
                      id="customer_service_work_hours"
                      v-model="profileForm.customer_service_work_hours"
                      type="text"
                      class="form-input"
                      placeholder="9 AM - 5 PM"
                      required
                    />
                  </div>

                  <div class="form-group">
                    <label for="customer_service_work_days" class="form-label">Customer Service Days</label>
                    <input
                      id="customer_service_work_days"
                      v-model="profileForm.customer_service_work_days"
                      type="text"
                      class="form-input"
                      placeholder="Saturday to Thursday"
                      required
                    />
                  </div>
                </div>

                <div class="form-row">
                  <div class="form-group">
                    <label for="office_work_hours" class="form-label">Office Hours</label>
                    <input
                      id="office_work_hours"
                      v-model="profileForm.office_work_hours"
                      type="text"
                      class="form-input"
                      placeholder="9 AM - 6 PM"
                      required
                    />
                  </div>

                  <div class="form-group">
                    <label for="office_work_days" class="form-label">Office Days</label>
                    <input
                      id="office_work_days"
                      v-model="profileForm.office_work_days"
                      type="text"
                      class="form-input"
                      placeholder="Sunday to Thursday"
                      required
                    />
                  </div>
                </div>
              </div>

              <!-- Additional Information -->
              <div class="form-section">
                <h3 class="section-title">
                  <span class="section-icon">📝</span>
                  Additional Information
                </h3>

                <div class="form-group">
                  <label for="notes" class="form-label">Notes</label>
                  <textarea
                    id="notes"
                    v-model="profileForm.notes"
                    class="form-textarea"
                    rows="4"
                    placeholder="Any additional notes or information..."
                  ></textarea>
                </div>
              </div>

              <!-- Form Actions -->
              <div class="form-actions">
                <button
                  type="button"
                  class="btn-secondary"
                  @click="openChangePasswordModal"
                >
                  <span class="button-icon">🔑</span>
                  Change Password
                </button>
                <button
                  type="submit"
                  class="btn-primary"
                  :disabled="updating"
                >
                  <span v-if="updating" class="loading-spinner-small"></span>
                  <span v-else class="save-icon">💾</span>
                  {{ updating ? 'Updating...' : 'Update Profile' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>

    <!-- Change Password Modal -->
    <div v-if="showChangePasswordModal" class="modal-overlay" @click.self="closeChangePasswordModal">
      <div class="modal-container">
        <div class="modal-header">
          <h3 class="modal-title">
            <span class="modal-icon">🔑</span>
            Change Password
          </h3>
          <button @click="closeChangePasswordModal" class="modal-close">×</button>
        </div>

        <form @submit.prevent="changePassword" class="modal-body">
          <div class="form-group">
            <label for="old_password" class="form-label">Current Password</label>
            <div class="password-input-wrapper">
              <input
                id="old_password"
                v-model="passwordForm.old_password"
                :type="showOldPassword ? 'text' : 'password'"
                class="form-input"
                :class="{ 'error': passwordErrors.old_password }"
                placeholder="Enter current password"
                required
              />
              <button
                type="button"
                @click="showOldPassword = !showOldPassword"
                class="toggle-password"
              >
                <svg v-if="!showOldPassword" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
                <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="m9.88 9.88a3 3 0 1 0 4.24 4.24"/>
                  <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 11 8 11 8a13.16 13.16 0 0 1-1.67 2.68"/>
                  <path d="M6.61 6.61A13.526 13.526 0 0 0 1 12s4 8 11 8a9.74 9.74 0 0 0 5.39-1.61"/>
                  <line x1="2" y1="2" x2="22" y2="22"/>
                </svg>
              </button>
            </div>
            <span v-if="passwordErrors.old_password" class="error-message">{{ passwordErrors.old_password }}</span>
          </div>

          <div class="form-group">
            <label for="new_password" class="form-label">New Password</label>
            <div class="password-input-wrapper">
              <input
                id="new_password"
                v-model="passwordForm.new_password"
                :type="showNewPassword ? 'text' : 'password'"
                class="form-input"
                :class="{ 'error': passwordErrors.new_password }"
                placeholder="Enter new password"
                required
              />
              <button
                type="button"
                @click="showNewPassword = !showNewPassword"
                class="toggle-password"
              >
                <svg v-if="!showNewPassword" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
                <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="m9.88 9.88a3 3 0 1 0 4.24 4.24"/>
                  <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 11 8 11 8a13.16 13.16 0 0 1-1.67 2.68"/>
                  <path d="M6.61 6.61A13.526 13.526 0 0 0 1 12s4 8 11 8a9.74 9.74 0 0 0 5.39-1.61"/>
                  <line x1="2" y1="2" x2="22" y2="22"/>
                </svg>
              </button>
            </div>
            <span v-if="passwordErrors.new_password" class="error-message">{{ passwordErrors.new_password }}</span>
          </div>

          <div class="form-group">
            <label for="confirm_new_password" class="form-label">Confirm New Password</label>
            <div class="password-input-wrapper">
              <input
                id="confirm_new_password"
                v-model="passwordForm.confirm_new_password"
                :type="showConfirmPassword ? 'text' : 'password'"
                class="form-input"
                :class="{ 'error': passwordErrors.confirm_new_password }"
                placeholder="Confirm new password"
                required
              />
              <button
                type="button"
                @click="showConfirmPassword = !showConfirmPassword"
                class="toggle-password"
              >
                <svg v-if="!showConfirmPassword" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
                <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="m9.88 9.88a3 3 0 1 0 4.24 4.24"/>
                  <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 11 8 11 8a13.16 13.16 0 0 1-1.67 2.68"/>
                  <path d="M6.61 6.61A13.526 13.526 0 0 0 1 12s4 8 11 8a9.74 9.74 0 0 0 5.39-1.61"/>
                  <line x1="2" y1="2" x2="22" y2="22"/>
                </svg>
              </button>
            </div>
            <span v-if="passwordErrors.confirm_new_password" class="error-message">{{ passwordErrors.confirm_new_password }}</span>
          </div>

          <div v-if="passwordChangeError" class="error-alert">
            {{ passwordChangeError }}
          </div>

          <div class="modal-footer">
            <button
              type="button"
              @click="closeChangePasswordModal"
              class="btn-secondary"
              :disabled="changingPassword"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="btn-primary"
              :disabled="changingPassword"
            >
              <span v-if="changingPassword" class="loading-spinner-small"></span>
              <span v-else class="button-icon">✓</span>
              {{ changingPassword ? 'Changing...' : 'Change Password' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Success Snackbar -->
    <div v-if="showSuccessNotification" class="snackbar success-snackbar">
      <div class="snackbar-content">
        <span class="snackbar-icon">✅</span>
        <span class="snackbar-message">Company profile updated successfully!</span>
      </div>
      <button @click="hideNotification" class="snackbar-close">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
        </svg>
      </button>
    </div>

    <!-- Error Snackbar -->
    <div v-if="showErrorNotification" class="snackbar error-snackbar">
      <div class="snackbar-content">
        <span class="snackbar-icon">❌</span>
        <span class="snackbar-message">Failed to update profile. Please try again.</span>
      </div>
      <button @click="hideNotification" class="snackbar-close">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
        </svg>
      </button>
    </div>
  </div>
</template>

<script>
import Sidebar from '../components/Sidebar.vue'
import TopHeader from '../components/TopHeader.vue'
import { DashboardService } from '../services/dashboard'

export default {
  name: 'CompanyProfileView',
  components: {
    Sidebar,
    TopHeader
  },
  data () {
    return {
      loading: true,
      updating: false,
      profileForm: {
        address: '',
        location_url: '',
        customer_service_work_hours: '',
        customer_service_work_days: '',
        office_work_hours: '',
        office_work_days: '',
        phone: '',
        notes: ''
      },
      originalProfile: {},
      showSuccessNotification: false,
      showErrorNotification: false,
      notificationTimeout: null,
      // Change Password Modal
      showChangePasswordModal: false,
      passwordForm: {
        old_password: '',
        new_password: '',
        confirm_new_password: ''
      },
      passwordErrors: {},
      passwordChangeError: '',
      changingPassword: false,
      showOldPassword: false,
      showNewPassword: false,
      showConfirmPassword: false
    }
  },
  async mounted () {
    await this.loadProfile()
  },
  methods: {
    async loadProfile () {
      try {
        this.loading = true
        const result = await DashboardService.getCompanyProfile()

        if (result.success && result.profile) {
          // Populate form with profile data
          this.profileForm = {
            address: result.profile.address || '',
            location_url: result.profile.location_url || '',
            customer_service_work_hours: result.profile.customer_service_work_hours || '',
            customer_service_work_days: result.profile.customer_service_work_days || '',
            office_work_hours: result.profile.office_work_hours || '',
            office_work_days: result.profile.office_work_days || '',
            phone: result.profile.phone || '',
            notes: result.profile.notes || ''
          }

          // Store original profile for reset functionality
          this.originalProfile = { ...this.profileForm }
        } else {
          console.error('Failed to load company profile:', result.error)
        }
      } catch (error) {
        console.error('Error loading company profile:', error)
      } finally {
        this.loading = false
      }
    },

    async updateProfile () {
      try {
        this.updating = true

        const result = await DashboardService.updateCompanyProfile(this.profileForm)

        if (result.success) {
          console.log('Company profile updated successfully')
          // Update original profile to new values
          this.originalProfile = { ...this.profileForm }

          // Show success notification
          this.displaySuccessNotification()
        } else {
          console.error('Failed to update company profile:', result.error)
          this.displayErrorNotification()
        }
      } catch (error) {
        console.error('Error updating company profile:', error)
        this.displayErrorNotification()
      } finally {
        this.updating = false
      }
    },

    displaySuccessNotification () {
      this.hideNotification() // Clear any existing notifications
      this.showSuccessNotification = true
      this.notificationTimeout = setTimeout(() => {
        this.hideNotification()
      }, 4000) // Auto hide after 4 seconds
    },

    displayErrorNotification () {
      this.hideNotification() // Clear any existing notifications
      this.showErrorNotification = true
      this.notificationTimeout = setTimeout(() => {
        this.hideNotification()
      }, 5000) // Auto hide after 5 seconds
    },

    hideNotification () {
      this.showSuccessNotification = false
      this.showErrorNotification = false
      if (this.notificationTimeout) {
        clearTimeout(this.notificationTimeout)
        this.notificationTimeout = null
      }
    }
  },

  beforeUnmount () {
    // Clean up timeout when component is destroyed
    if (this.notificationTimeout) {
      clearTimeout(this.notificationTimeout)
    }
  }
}
</script>

<style scoped>
.company-profile-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-secondary);
  transition: background-color 0.3s ease;
}

.main-content {
  flex: 1;
  margin-left: 280px;
  display: flex;
  flex-direction: column;
}

.page-content {
  flex: 1;
  padding: 32px;
}

.profile-container {
  max-width: 800px;
  margin: 0 auto;
}

/* Page Header */
.page-header {
  margin-bottom: 32px;
}

.header-content {
  text-align: center;
}

.page-title {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 8px;
  transition: color 0.3s ease;
}

.page-icon {
  font-size: 32px;
}

.page-description {
  font-size: 16px;
  color: var(--text-secondary);
  margin: 0;
  transition: color 0.3s ease;
}

/* Profile Form */
.profile-form-container {
  background: var(--bg-primary);
  border-radius: 16px;
  border: 1px solid var(--border-primary);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

.profile-form {
  padding: 32px;
}

.form-section {
  margin-bottom: 40px;
}

.form-section:last-of-type {
  margin-bottom: 32px;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 20px;
  padding-bottom: 8px;
  border-bottom: 2px solid var(--border-primary);
  transition: color 0.3s ease, border-color 0.3s ease;
}

.section-icon {
  font-size: 20px;
  opacity: 0.8;
}

.save-icon {
  font-size: 16px;
  margin-right: 4px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
}

.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 6px;
  transition: color 0.3s ease;
}

.field-icon {
  font-size: 14px;
  opacity: 0.7;
}

.form-input,
.form-textarea {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid var(--border-secondary);
  border-radius: 8px;
  font-size: 14px;
  color: var(--text-primary);
  background: var(--bg-primary);
  transition: all 0.2s;
  box-sizing: border-box;
}

.form-input:focus,
.form-textarea:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-textarea {
  resize: vertical;
  min-height: 100px;
}

.form-help {
  font-size: 12px;
  color: var(--text-secondary);
  margin-top: 4px;
  transition: color 0.3s ease;
}

/* Form Actions */
.form-actions {
  display: flex;
  justify-content: center;
  padding-top: 20px;
  border-top: 1px solid var(--border-primary);
  transition: border-color 0.3s ease;
}

.btn-primary,
.btn-secondary {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid;
  min-width: 120px;
  justify-content: center;
}

.btn-primary {
  background: #3b82f6;
  color: white;
  border-color: #3b82f6;
}

.btn-primary:hover:not(:disabled) {
  background: #2563eb;
  border-color: #2563eb;
}

.btn-primary:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-secondary {
  background: var(--bg-primary);
  color: var(--text-secondary);
  border-color: var(--border-secondary);
}

.btn-secondary:hover:not(:disabled) {
  background: var(--bg-tertiary);
  border-color: var(--border-primary);
  color: var(--text-primary);
}

/* Loading States */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 64px;
  color: var(--text-secondary);
  text-align: center;
  transition: color 0.3s ease;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--border-primary);
  border-top: 3px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  transition: border-color 0.3s ease;
}

.loading-spinner-small {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Snackbar Notifications */
.snackbar {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  z-index: 3000;
  min-width: 300px;
  max-width: 500px;
  animation: slideUp 0.3s ease-out;
  backdrop-filter: blur(10px);
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

.success-snackbar {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.error-snackbar {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.snackbar-content {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
}

.snackbar-icon {
  font-size: 18px;
  flex-shrink: 0;
}

.snackbar-message {
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
}

.snackbar-close {
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.8;
}

.snackbar-close:hover {
  opacity: 1;
  background: rgba(255, 255, 255, 0.1);
  transform: scale(1.1);
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
    padding-top: 64px;
  }

  .page-content {
    padding: 16px;
  }

  .profile-form {
    padding: 20px;
  }

  .form-row {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .page-title {
    font-size: 24px;
  }

  .btn-primary {
    width: 100%;
    min-width: auto;
  }

  .snackbar {
    left: 16px;
    right: 16px;
    transform: none;
    min-width: auto;
    max-width: none;
  }

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
}
</style>
